import React, { useState } from "react";

import { GraphicApiResponse } from "src/types/graphic";
import showToast from "@utils/toast";
import { updateRequest } from "src/service/api-services/api-services";
import { useNavigate } from "react-router-dom";

export interface IMediaAssetUpdateProps {
  assetToUpdate: {
    id: string;
    localText: string;
    translation: string;
  };
  assetTypeToUpdate: "Audio" | "Video";
  setIsModalOpen: (val: boolean) => void;
}

export default function MediaAssetMetaUpdate({
  assetToUpdate,
  assetTypeToUpdate,
  setIsModalOpen,
}: IMediaAssetUpdateProps) {
  const { id, ...assetWithoutId } = assetToUpdate;
  const [isLoading, setLoading] = useState<Boolean>(false);
  const [data, setData] = useState({
    ...assetWithoutId,
  });

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setData((prev) => ({ ...prev, [name]: value }));
  };

  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const assetPath = assetTypeToUpdate === "Audio" ? "audios" : "videos";

    try {
      const response = await updateRequest<GraphicApiResponse>(
        `https://lams-api.pluritongues.com/${assetPath}`,
        { id, ...data },
      );
      if (response.status === "success") {
        setLoading(false);
        setIsModalOpen(false);
        showToast("Asset was updated successfully!", true);
        return navigate(0);
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Apologies, there was an error updating assets";
      setLoading(false);
      showToast(message, false);
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/40">
      <div className="bg-white rounded-lg w-full max-w-md p-6 shadow-lg">
        <div className="flex justify-between items-center border-b pb-2 mb-4">
          <h2 className="text-lg font-semibold">
            {assetTypeToUpdate === "Audio"
              ? "Edit Audio Asset"
              : "Edit Video Asset"}
          </h2>

          <button
            onClick={handleModalClose}
            className="text-gray-600 hover:text-black"
          >
            ✕
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Local Text
            </label>
            <input
              name="localText"
              value={data.localText}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring focus:ring-brand"
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Translation
            </label>
            <textarea
              name="translation"
              value={data.translation}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring focus:ring-brand"
              rows={3}
              required
            />
          </div>

          <button
            disabled={isLoading ? true : false}
            type="submit"
            className="w-full bg-brand cursor-pointer text-white py-2 rounded font-medium hover:bg-brand-dark transition"
          >
            {isLoading ? "Updating...." : "Click to edit"}
          </button>
        </form>
      </div>
    </div>
  );
}
